import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import type {
  FactProposal,
  FactProposalDecisionItem,
  FactProposalDecisionResult,
} from "@/types/chat.type";
import { brandUniverseChatProposalsKey } from "./useBrandUniverseChatProposals";
import { brandUniversePendingFactProposalsKey } from "./useBrandUniversePendingFactProposals";
import { brandUniverseGraphKey } from "./universeCache";

export const decideBrandFactProposalsMutationKey = (brandId: string) =>
  ["decideBrandFactProposals", brandId] as const;

/**
 * Decides pending fact proposals that may span multiple chat sessions (brand-global
 * review overlay). Groups by sessionId, posts one decision request per session,
 * then invalidates the brand pending list, per-session proposal caches, and the
 * universe graph (accepts write facts).
 */
export function useDecideBrandFactProposals(brandId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: decideBrandFactProposalsMutationKey(brandId),
    mutationFn: async (vars: {
      proposals: FactProposal[];
      decisions: FactProposalDecisionItem[];
    }) => {
      const decisionById = new Map(vars.decisions.map((d) => [d.proposalId, d]));
      const bySession = new Map<string, FactProposalDecisionItem[]>();
      for (const p of vars.proposals) {
        const d = decisionById.get(p.id);
        if (!d) continue;
        const list = bySession.get(p.sessionId) ?? [];
        list.push(d);
        bySession.set(p.sessionId, list);
      }

      const results: FactProposalDecisionResult[] = [];
      for (const [sessionId, decisions] of bySession) {
        const res = await axiosInstance.post<{ results: FactProposalDecisionResult[] }>(
          `/brand-agent/${brandId}/sessions/${sessionId}/fact-proposal/decision`,
          { decisions },
        );
        results.push(...res.data.results);
      }
      return results;
    },
    // Refetch graph first, then pending. If pending cleared before the graph
    // landed, the tray vanished and the universe briefly looked empty.
    onSuccess: async (_data, vars) => {
      const sessionIds = [...new Set(vars.proposals.map((p) => p.sessionId))];
      await queryClient.invalidateQueries({
        queryKey: brandUniverseGraphKey(brandId),
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: brandUniversePendingFactProposalsKey(brandId),
        }),
        ...sessionIds.map((sessionId) =>
          queryClient.invalidateQueries({
            queryKey: brandUniverseChatProposalsKey(brandId, sessionId),
          }),
        ),
      ]);
    },
  });
}
